import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus } from 'lucide-react';

/**
 * PasswordStrengthMeter — deterministic, offline password scoring.
 *
 * Score buckets (0..4):
 *   0 = empty  → hidden meter, rules only after the user starts typing
 *   1 = weak
 *   2 = fair
 *   3 = good
 *   4 = strong
 *
 * Rules surfaced to the user:
 *   • 8+ characters   (matches existing signup validation)
 *   • Letters + numbers (matches existing signup validation)
 *   • Mix of upper- and lowercase letters (bonus)
 *   • A symbol       (bonus)
 *   • 12+ characters (bonus)
 *
 * No dependencies. No network. No storage. Purely local computation.
 */

const LEVEL_KEYS = ['empty', 'weak', 'fair', 'good', 'strong'];
const LEVEL_COLORS = [
  'bg-slate-200 dark:bg-slate-700', // empty
  'bg-red-500', // weak
  'bg-amber-500', // fair
  'bg-lime-500', // good
  'bg-emerald-500', // strong
];

function scorePassword(password) {
  const value = password || '';
  if (!value) return { score: 0, checks: {}, entropy: 0 };

  const checks = {
    length8: value.length >= 8,
    length12: value.length >= 12,
    hasLetter: /[A-Za-z]/.test(value),
    hasNumber: /\d/.test(value),
    hasMixedCase: /[a-z]/.test(value) && /[A-Z]/.test(value),
    hasSymbol: /[^A-Za-z0-9]/.test(value),
    notCommon: !/^(?:password|123456|qwerty|letmein|welcome|admin|iloveyou)\d*$/i.test(
      value.trim(),
    ),
    notRepeated: !/^(.)\1+$/.test(value),
    notSequential: !/(?:0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef|qwer|wert)/i.test(
      value,
    ),
  };

  // Weighted score → 0..4 bucket.
  let raw = 0;
  if (checks.length8) raw += 2;
  if (checks.length12) raw += 1;
  if (checks.hasLetter && checks.hasNumber) raw += 1;
  if (checks.hasMixedCase) raw += 1;
  if (checks.hasSymbol) raw += 1;
  if (!checks.notCommon) raw = Math.min(raw, 1);
  if (!checks.notRepeated) raw = Math.min(raw, 1);
  if (!checks.notSequential) raw = Math.min(raw, 2);

  let score;
  if (raw <= 1) score = 1; // weak
  else if (raw <= 3) score = 2; // fair
  else if (raw <= 5) score = 3; // good
  else score = 4; // strong

  return { score, checks };
}

function Rule({ ok, label }) {
  return (
    <li
      className={`flex items-center gap-1.5 text-xs ${
        ok
          ? 'text-emerald-700 dark:text-emerald-300'
          : 'text-slate-500 dark:text-slate-400'
      }`}
    >
      {ok ? (
        <Check className="w-3.5 h-3.5" />
      ) : (
        <Minus className="w-3.5 h-3.5" />
      )}
      <span>{label}</span>
    </li>
  );
}

export default function PasswordStrengthMeter({ password, className = '' }) {
  const { t } = useTranslation();
  const { score, checks } = useMemo(() => scorePassword(password), [password]);
  const level = LEVEL_KEYS[score];
  const barColor = LEVEL_COLORS[score];

  const hasInput = (password || '').length > 0;

  return (
    <div
      data-testid="password-strength-meter"
      aria-live="polite"
      className={`mt-2 ${className}`}
    >
      <div
        className="flex items-center gap-1"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={4}
        aria-valuenow={score}
        aria-valuetext={t(`auth.strength.level.${level}`)}
      >
        {[1, 2, 3, 4].map((seg) => (
          <div
            key={seg}
            data-testid={`strength-segment-${seg}`}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
              score >= seg ? barColor : 'bg-slate-200 dark:bg-slate-700'
            }`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex items-center justify-between">
        <span
          data-testid="password-strength-label"
          className={`text-xs font-semibold ${
            score >= 3
              ? 'text-emerald-700 dark:text-emerald-300'
              : score === 2
              ? 'text-amber-700 dark:text-amber-300'
              : score === 1
              ? 'text-red-600 dark:text-red-400'
              : 'text-slate-400'
          }`}
        >
          {hasInput ? t(`auth.strength.level.${level}`) : ''}
        </span>
      </div>
      {hasInput && (
        <ul
          data-testid="password-strength-rules"
          className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5"
        >
          <Rule ok={checks.length8} label={t('auth.strength.rules.length8')} />
          <Rule
            ok={checks.hasLetter && checks.hasNumber}
            label={t('auth.strength.rules.lettersNumbers')}
          />
          <Rule
            ok={checks.hasMixedCase}
            label={t('auth.strength.rules.mixedCase')}
          />
          <Rule ok={checks.hasSymbol} label={t('auth.strength.rules.symbol')} />
          <Rule ok={checks.length12} label={t('auth.strength.rules.length12')} />
        </ul>
      )}
    </div>
  );
}

export { scorePassword };
